import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Grid,
  useMediaQuery,
  Theme,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  alpha,
} from "@mui/material/styles";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FeedbackIcon from "@mui/icons-material/Feedback";
import HealingIcon from "@mui/icons-material/Healing";

// Custom Theme with Primary Color
const theme = createTheme({
  palette: {
    primary: {
      main: "#bb0000",
    },
    background: {
      default: "#f4f4f4",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
}));

const ResponsiveCard = styled(Card)(() => ({
  // width: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: `0 4px 20px rgba(187,0,0,0.2)`,
  },
}));

interface Property {
  name: string;
  address: string;
  area: string;
  latitude: number;
  longitude: number;
  problem: string;
  problemSummary: string;
}

interface BasicInformation {
  image: string;
  name: string;
  email: string;
  phoneNumber: string;
  role: string;
}

interface VastuAnalysis {
  observation: string;
}

interface Remedy {
  action: string;
}

interface UserData {
  basicInformation: BasicInformation;
  properties: Property[];
  vastuAnalysis: VastuAnalysis[];
  remedies: Remedy[];
  feedbacks: string[];
}

// interface PropertyMapProps {
//   lat: number;
//   lng: number;
// }

const SpecificUserPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const PropertyMap: React.FC<{ lat: number; lng: number }> = ({
    lat,
    lng,
  }) => {
    const mapContainerStyle = {
      height: isMobile ? "200px" : "250px",
      width: "100%",
      borderRadius: 16,
    };

    return (
      <LoadScript googleMapsApiKey="AIzaSyCBMjLiUIKGjqXDuEq0LnSJNzMgvGmyBSQ">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{ lat, lng }}
          zoom={15}
        >
          <Marker position={{ lat, lng }} />
        </GoogleMap>
      </LoadScript>
    );
  };

  // User data remains the same as previous example

  const userData: UserData = {
    basicInformation: {
      image: "https://via.placeholder.com/150",
      name: "John Doe",
      email: "johndoe@example.com",
      phoneNumber: "+1234567890",
      role: "User",
    },
    properties: [
      {
        name: "Sunshine Villa",
        address: "123 Main Street, Springfield",
        area: "1200 sq ft",
        latitude: 37.7749,
        longitude: -122.4194,
        problem: "Water leakage in basement",
        problemSummary: "Basement has severe water leakage during heavy rains.",
      },
      {
        name: "Moonlight Apartments",
        address: "45 Elm Street, Springfield",
        area: "800 sq ft",
        latitude: 40.7128,
        longitude: -74.006,
        problem: "Roof damage",
        problemSummary: "The roof requires urgent repair due to storm damage.",
      },
    ],
    vastuAnalysis: [
      { observation: "Kitchen in North" },
      { observation: "Entrance in West" },
      { observation: "Toilet in Southeast" },
    ],
    remedies: [
      { action: "Place a mirror on the north wall of the living room" },
      { action: "Avoid red color in the kitchen" },
      { action: "Use wooden doors for the entrance facing west" },
    ],
    feedbacks: [
      "Excellent service and timely response.",
      "The analysis was very helpful for my family.",
      "I would recommend this service to my friends.",
      "The analysis was very helpful for my family.",
      "I would recommend this service to my friends.",
    ],
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log(event);
    setActiveTab(newValue);
  };

  const tabIcons = [
    <HomeIcon />,
    <AssessmentIcon />,
    <HealingIcon />,
    <FeedbackIcon />,
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          bgcolor: "background.default",
          overflowY: "auto",
        }}
      >
        <Grid container sx={{ height: "100%" }}>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* User Basic Information */}
            <Box
              sx={{
                backgroundImage: `linear-gradient(145deg, ${alpha(
                  theme.palette.primary.main,
                  0.8
                )}, ${alpha(theme.palette.primary.main, 0.6)})`,

                // color: "white",
                p: { xs: 2, sm: 3, md: 4 },
                textAlign: "center",
              }}
            >
              <Avatar
                src={userData.basicInformation.image}
                sx={{
                  width: { xs: 100, sm: 120, md: 150 },
                  height: { xs: 100, sm: 120, md: 150 },
                  mb: 2,
                  margin: "0 auto",
                  border: "4px solid white",
                }}
              />
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 600, color: "white" }}
              >
                {userData.basicInformation.name}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                {userData.basicInformation.role}
              </Typography>
              <Grid
                container
                spacing={1}
                justifyContent="center"
                sx={{ mt: { xs: 1, sm: 2 } }}
              >
                <Grid item>
                  <Typography variant="body2" color="inherit">
                    📧 {userData.basicInformation.email}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="inherit">
                    📱 {userData.basicInformation.phoneNumber}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <StyledTabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
            >
              {["Properties", "Feedback"].map((label, index) => (
                <Tab
                  key={label}
                  icon={tabIcons[index]}
                  label={!isMobile ? label : undefined}
                  iconPosition="start"
                />
              ))}
            </StyledTabs>

            {/* Content Area */}
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: { xs: 1, sm: 2, md: 3 },
                bgcolor: "background.default",
              }}
            >
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  {userData.properties.map((property, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <ResponsiveCard>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <LocationOnIcon
                              sx={{ mr: 2, color: "primary.main" }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {property.name}
                            </Typography>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Address:</strong> {property.address}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Area:</strong> {property.area}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="error.main">
                                <strong>Problem:</strong> {property.problem}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Summary:</strong>{" "}
                                {property.problemSummary}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 2 }}>
                            <PropertyMap
                              lat={property.latitude}
                              lng={property.longitude}
                            />
                          </Box>
                        </CardContent>
                      </ResponsiveCard>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* {activeTab === 1 && (
                <List>
                  {userData.vastuAnalysis.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor:
                          index % 2 ? "rgba(187,0,0,0.05)" : "transparent",
                        borderRadius: 2,
                      }}
                    >
                      <ListItemText
                        primary={item.observation}
                        primaryTypographyProps={{
                          color: "text.secondary",
                          fontWeight: 500,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )} */}

              {/* {activeTab === 2 && (
                <List>
                  {userData.remedies.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor:
                          index % 2 ? "rgba(187,0,0,0.05)" : "transparent",
                        borderRadius: 2,
                      }}
                    >
                      <ListItemText
                        primary={item.action}
                        primaryTypographyProps={{
                          color: "text.secondary",
                          fontWeight: 500,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )} */}

              {activeTab === 1 && (
                <List>
                  {userData.feedbacks.map((feedback, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor:
                          index % 2 ? "rgba(187,0,0,0.05)" : "transparent",
                        borderRadius: 2,
                        mb: 1,
                      }}
                    >
                      <ListItemText
                        primary={feedback}
                        primaryTypographyProps={{
                          color: "text.secondary",
                          fontStyle: "italic",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default SpecificUserPage;
